import React, {
  useMemo,
  useCallback,
  useRef,
  useEffect,
  useState,
  Fragment,
} from 'react';
import { Editor, Transforms, Range, createEditor, Node } from 'slate';
import { withHistory } from 'slate-history';
import { Slate, Editable, ReactEditor, withReact } from 'slate-react';
import { Input, Grid, Button } from 'semantic-ui-react';
import Portal from '../Portal.jsx';
import FilterModal from './FilterModal.jsx';
import useToggle from '../hooks/useToggle.jsx';

const defaultSyntax = [
  '=',
  '!=',
  'equals',
  'is not equal',
  'contains',
  'in',
  'not in',
  'does not contain',
  'and',
  'or',
  '&&',
  '||',
];

/**
 * Serialize slate node to plain text
 */
const serialize = (nodes) => {
  return nodes
    ? nodes
        .map((n) => Node.string(n))
        .join('')
        .trimStart()
    : '';
};

/**
 * Deserialize string into a Slate-compatible json
 */
const buildValue = (value) => {
  return [
    {
      children: [{ text: value }],
    },
  ];
};

/**
 * Inserts the option selected for autocomplete into the editor's value
 */
const insertTip = (editor, selectedOption, spaceBefore) => {
  Transforms.insertText(editor, `${spaceBefore ? ' ' : ''}${selectedOption} `);
  Transforms.move(editor);
};

const Element = (props) => {
  const { attributes, children } = props;

  return <span {...attributes}>{children}</span>;
};

export default function RQLSearchInput(props) {
  const ref = useRef();
  const didMountRef = useRef(false);
  const [value, setValue] = useState(buildValue(props.filter.query || ''));
  const [beforeIndex, setBeforeIndex] = useState();
  const [target, setTarget] = useState();
  const [index, setIndex] = useState(0);
  const [search, setSearch] = useState('');
  const [spaceBefore, setSpaceBefore] = useState();
  const [recentBlur, setBlur] = useState(false);
  const [lastTarget, setLastTarget] = useState(target);
  const renderElement = useCallback((_props) => <Element {..._props} />, []);
  const editor = useMemo(() => withReact(withHistory(createEditor())), []);
  const syntax = [...defaultSyntax, ...props.customSyntax];
  const serializedValue = useMemo(() => serialize(value), [value]);
  const toggleFilterModal = useToggle(false);

  const onSubmit = useCallback(() => {
    return serializedValue.length === 0 ? undefined : props.onSubmit();
  }, [serializedValue]);

  const openFilterModal = useCallback(() => {
    toggleFilterModal.setTrue();
    onSubmit();
  }, [serializedValue]);

  /* Filters a max of 10 available options that can autocomplete the current search */
  const options = syntax
    .filter((opt) => opt.toLowerCase().startsWith(search.toLowerCase()))
    .slice(0, 10);

  const onBlur = useCallback(() => {
    setLastTarget(target);
    setBlur(true);
    // Timeout is needed because on blur target is set to null so
    // the click does not happen, the component re-renders before that.
    // This timeout have the only purpose of allowing users to click on
    // typeahead options without a target
    setTimeout(() => setBlur(false), 200);
  });

  const onHoverItem = useCallback((selectedIndex) => {
    setIndex(selectedIndex);
  });

  const onSelectItem = useCallback((e, selectedIndex) => {
    e.preventDefault();
    Transforms.select(editor, lastTarget);
    insertTip(editor, options[selectedIndex], spaceBefore);
    setTarget(null);
    ReactEditor.focus(editor);
  });

  const onKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' && !target) {
        event.preventDefault();
        return onSubmit();
      }

      if (!target) {
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const prevIndex = index >= options.length - 1 ? 0 : index + 1;

        return setIndex(prevIndex);
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        const nextIndex = index <= 0 ? options.length - 1 : index - 1;

        return setIndex(nextIndex);
      }

      if (event.key === 'Tab' || event.key === 'Enter') {
        event.preventDefault();
        if (options.length == 0) {
          return onSubmit();
        }

        Transforms.select(editor, target);
        insertTip(editor, options[index], spaceBefore);
        return setTarget(null);
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        return setTarget(null);
      }
    },
    [index, search, target],
  );

  const onChange = useCallback((changedValue) => {
    setValue(changedValue);
    const { selection } = editor;

    if (selection && Range.isCollapsed(selection)) {
      const [start] = Range.edges(selection);

      let wordBefore = Editor.before(editor, start, { unit: 'word' }),
        before = wordBefore && Editor.before(editor, wordBefore),
        beforeRange = before && Editor.range(editor, before, start),
        beforeText = beforeRange && Editor.string(editor, beforeRange);

      if (beforeText && beforeText.includes('_')) {
        wordBefore = beforeIndex;
        before = wordBefore && Editor.before(editor, wordBefore);
        beforeRange = before && Editor.range(editor, before, start);
        beforeText = beforeRange && Editor.string(editor, beforeRange);
      }
      const beforeMatch = beforeText && beforeText.match(/^ (\w+)$/);
      const after = Editor.after(editor, start);
      const afterRange = Editor.range(editor, start, after);
      const afterText = Editor.string(editor, afterRange);
      const afterMatch = afterText.match(/^(\s|$)/);

      setSpaceBefore(true);
      setBeforeIndex(wordBefore);

      if (beforeMatch && afterMatch) {
        setTarget(beforeRange);
        setSearch(beforeMatch[1]);
        setIndex(0);
        return;
      }

      if (
        before &&
        !beforeMatch &&
        !beforeText.match(/^(\s|$)/) &&
        afterMatch
      ) {
        const backtrack = Editor.before(editor, before, { unit: 'word' });
        const newPoint = backtrack && Editor.before(editor, backtrack);
        const newRange = newPoint && Editor.range(editor, newPoint, start);
        const newWord = newRange && Editor.string(editor, newRange);
        const newMatch = newWord && newWord.match(/ (\w+)$/);

        if (newMatch) {
          setTarget(newRange);
          setSearch(newMatch[1]);
          setIndex(0);
          return;
        }
      }

      if (!before || (before && beforeText.match(/^_/))) {
        const rangeEnd = before
          ? Editor.before(editor, before, { unit: 'word' })
          : wordBefore;
        const newRange = Editor.range(editor, start, rangeEnd);
        const newWord = newRange && Editor.string(editor, newRange);
        const newMatch = newWord.match(/(\w+)$/);
        const wordEnd = !after || afterText === ' ';

        if (newMatch && !newWord.match(/^(\s|$)/) && wordEnd) {
          setSpaceBefore(false);
          setTarget(newRange);
          setSearch(newMatch[1]);
          setIndex(0);
          return;
        }
      }
    }
    setTarget(null);
  });

  useEffect(() => {
    // if querySucceeded false filter modal cannot be opened
    if (!props.querySucceeded && toggleFilterModal.value) {
      toggleFilterModal.setFalse();
    }
  }, [props.querySucceeded]);

  useEffect(() => {
    if (
      didMountRef.current &&
      props.filter.query &&
      props.filter.query != serializedValue
    ) {
      setValue(buildValue(props.filter.query));
    }
  }, [props.filter.query]);

  useEffect(() => {
    if (didMountRef.current && serializedValue != props.filter.query) {
      if (serializedValue.length === 0) {
        props.onClear();
      } else {
        props.onChange(serializedValue);
      }
    } else {
      didMountRef.current = true;
    }
  }, [serializedValue]);

  /**
   * Positions the auto complete div element that lists the available options to select
   */
  useEffect(() => {
    if (target && options.length > 0) {
      const el = ref.current;
      const domRange = ReactEditor.toDOMRange(editor, target);
      const rect = domRange.getBoundingClientRect();

      el.style.top = `${rect.top + window.pageYOffset + 24}px`;
      el.style.left = `${rect.left + window.pageXOffset}px`;
    }
  }, [options.length, editor, index, search, target]);

  useEffect(() => {
    // Only for testing purposes
    if (
      process.env.NODE_ENV === 'test' &&
      typeof props.TEST_setValueCallback !== 'undefined'
    ) {
      props.TEST_setValueCallback();
    }
  }, [props]);

  return (
    <Fragment>
      <Input
        action
        className='RQLInput'
        size='large'
        type='text'
      >
        <input
          readOnly
          id='rql_search_value'
          type='hidden'
          value={serializedValue}
        />

        <Grid
          padded
          className='RQLInputGrid'
        >
          <Slate
            editor={editor}
            initialValue={value}
            onChange={onChange}
          >
            <Editable
              className='RQLSearchEditable'
              placeholder='Enter some text...'
              renderElement={renderElement}
              onBlur={onBlur}
              onKeyDown={onKeyDown}
            />
            {(target || recentBlur) && options.length > 0 && (
              <Portal node={document.body}>
                <div
                  ref={ref}
                  className='RQLAutocomplete'
                >
                  {options.map((opt, i) => (
                    <div
                      key={opt}
                      className='RQLAutocompleteItem'
                      style={{
                        background: i === index ? '#B4D5FF' : 'transparent',
                      }}
                      onClick={(e) => onSelectItem(e, i)}
                      onMouseEnter={() => onHoverItem(i)}
                    >
                      {opt}
                    </div>
                  ))}
                </div>
              </Portal>
            )}
          </Slate>
          {props.children}
        </Grid>

        <Button
          size='big'
          type='submit'
          onClick={onSubmit}
        >
          Search
        </Button>
      </Input>

      {props.canCreateFilter && serializedValue && (
        <a
          className={`RQLSaveFilter ${props.error && 'disabled'}`}
          disabled={!props.error}
          onClick={openFilterModal}
        >
          {props.filter.id ? 'Update' : 'Save'} Filter
        </a>
      )}

      <FilterModal
        close={toggleFilterModal.setFalse}
        filter={props.filter}
        isOpen={toggleFilterModal.value && props.querySucceeded} // if semaphore locked filter modal cannot be opened
        query={serializedValue}
        onSave={props.onSave}
      />
    </Fragment>
  );
}
